import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Avatar, Box, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../components/Flex";
import Text from "../../../../../../components/Text";
import { applyDefaultColumnSettings } from "../../../../../../utils/utils";

const INSURANCE_LISTING_TOP_COLUMNS = applyDefaultColumnSettings([
  { field: "id", headerName: "Payer ID", minWidth: 100, flex: 1 },
  { field: "payerName", headerName: "Payer Name", minWidth: 200, flex: 1.5 },
  {
    field: "days_0_30",
    headerName: "0-30 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_31_60",
    headerName: "31-60 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_61_90",
    headerName: "61-90 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_91_120",
    headerName: "91-120 Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "days_120_plus",
    headerName: "120+ Days",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "total",
    headerName: "Total",
    minWidth: 100,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
]);

const INSURANCE_LISTING_TOP_ROW = [
  {
    id: "A8431",
    payerName: "Hartford Financial Services Group",
    days_0_30: 10,
    days_31_60: 20,
    days_61_90: 20,
    days_91_120: 10,
    days_120_plus: 53,
    total: 95,
  },
];

// Main table columns for insurance listing
const INSURANCE_LISTING_DETAIL_COLUMNS = [
  { field: "claimNumber", headerName: "Claim Number", minWidth: 120, flex: 1 },
  {
    field: "patientName",
    headerName: "Patient Name",
    minWidth: 200,
    flex: 1.5,
    renderCell: ({ row }: { row: any }) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar src={row.avatar} sx={{ width: 32, height: 32 }} />
        <Box>
          <Text color="primary.main" fontWeight={600} fontSize={15}>
            {row.patientName}
          </Text>
          <Text fontSize={13} color="grey.700">
            {row.ageGender}
          </Text>
        </Box>
      </Box>
    ),
  },
  {
    field: "dateOfService",
    headerName: "Date of Service",
    minWidth: 140,
    flex: 1,
  },
  {
    field: "dateOfBilling",
    headerName: "Date of Billing",
    minWidth: 140,
    flex: 1,
  },
  {
    field: "dateOfClaimSubmission",
    headerName: "Date of Claim Submission",
    minWidth: 180,
    flex: 1,
  },
  {
    field: "billAmount",
    headerName: "Bill Amount",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "claimAmount",
    headerName: "Claim Amount",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "paidAmount",
    headerName: "Paid Amount",
    minWidth: 120,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
  {
    field: "balanceAmount",
    headerName: "Balance Amount",
    minWidth: 140,
    flex: 1,
    renderCell: ({ value }: any) => `$${value}`,
  },
];

const INSURANCE_LISTING_DETAIL_DUMMY_DATA = [
  {
    id: 1,
    claimNumber: "23453",
    patientName: "Henna West",
    avatar: "/avatars/henna.png",
    ageGender: "65 yrs-female",
    dateOfService: "Dec 10 2021",
    dateOfBilling: "Dec 15 2021",
    dateOfClaimSubmission: "Dec 20 2021",
    billAmount: 100,
    claimAmount: 40,
    paidAmount: 30,
    balanceAmount: 30,
  },
  {
    id: 2,
    claimNumber: "30867",
    patientName: "Demelza Robins",
    avatar: "/avatars/demelza.png",
    ageGender: "59 yrs-female",
    dateOfService: "Nov 15 2021",
    dateOfBilling: "Nov 18 2021",
    dateOfClaimSubmission: "Nov 20 2021",
    billAmount: 120,
    claimAmount: 60,
    paidAmount: 40,
    balanceAmount: 20,
  },
  {
    id: 3,
    claimNumber: "43568",
    patientName: "Mary Riddle",
    avatar: "/avatars/mary.png",
    ageGender: "50 yrs-female",
    dateOfService: "Oct 01 2021",
    dateOfBilling: "Oct 06 2021",
    dateOfClaimSubmission: "Oct 12 2021",
    billAmount: 80,
    claimAmount: 10,
    paidAmount: 20,
    balanceAmount: 50,
  },
  {
    id: 4,
    claimNumber: "97896",
    patientName: "Ron Weasley",
    avatar: "/avatars/ron.png",
    ageGender: "61 yrs-female",
    dateOfService: "Sep 23 2021",
    dateOfBilling: "Sep 26 2021",
    dateOfClaimSubmission: "Sep 29 2021",
    billAmount: 150,
    claimAmount: 100,
    paidAmount: 10,
    balanceAmount: 40,
  },
  {
    id: 5,
    claimNumber: "36426",
    patientName: "Ludo Bagman",
    avatar: "/avatars/ludo.png",
    ageGender: "58 yrs-female",
    dateOfService: "Aug 29 2021",
    dateOfBilling: "Aug 31 2021",
    dateOfClaimSubmission: "Sep 03 2021",
    billAmount: 200,
    claimAmount: 100,
    paidAmount: 10,
    balanceAmount: 90,
  },
];

const InsuranceListingTable = () => {
  const navigate = useNavigate();
  const topColumnHeight = 130;
  return (
    <Box>
      <Flex alignItems="center" mb={2} mt={1}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Text fontWeight={700} fontSize={22} ml={1}>
          Insurance Listing
        </Text>
      </Flex>
      <Box height={`${topColumnHeight}px`}>
        <CustomDataGridV2
          rows={INSURANCE_LISTING_TOP_ROW}
          hideFooter
          rowCount={INSURANCE_LISTING_TOP_ROW.length}
          getRowId={(row) => row.id}
          rowHeight={40}
          columns={INSURANCE_LISTING_TOP_COLUMNS}
        />
      </Box>
      <Box height={`calc(100vh - ${topColumnHeight + 168}px)`}>
        <CustomDataGridV2
          rows={INSURANCE_LISTING_DETAIL_DUMMY_DATA}
          columns={INSURANCE_LISTING_DETAIL_COLUMNS}
          getRowId={(row) => row.id}
          rowHeight={60}
        />
      </Box>
    </Box>
  );
};

export default InsuranceListingTable;
